import { TelegramAltIcon } from "@/components/Icons"
import { Modal } from "@/components/Modal"
import { SHARED_DATA_STATUS } from "@/domain/sharedData/constants"
import { useGenericErrorToast } from "@/hooks/useGenericErrorToast"
import {
  type SharedDataJsonldReadCollectionSharedData,
  type SharedDataPatchSharedDataInputWriteItemSharedData,
  getGetUserSharedDataQueryKey,
  getGetUserSharedDatasQueryKey,
  useUpdateUserSharedData,
} from "@/networking/api"
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  ModalBody,
  ModalFooter,
  Textarea,
} from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import SharedEntityName from "./SharedEntityName"

type FormType = Partial<SharedDataPatchSharedDataInputWriteItemSharedData>

type Props = {
  isOpen: boolean
  onClose: () => void
  sharedEntity: SharedDataJsonldReadCollectionSharedData
}

export default function ShareCancelModal({ isOpen, onClose, sharedEntity }: Props) {
  const defaultValues = {
    messageAnswer: "",
  }

  const { handleSubmit, register } = useForm<FormType>({
    defaultValues,
  })

  const { mutate, isPending } = useUpdateUserSharedData()
  const queryClient = useQueryClient()
  const genericErrorToast = useGenericErrorToast()

  const postShareRequest = (formData: FormType) => {
    mutate(
      {
        data: {
          ...formData,
          id: sharedEntity.id,
          status: SHARED_DATA_STATUS.CANCELLED,
        },
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getGetUserSharedDataQueryKey(sharedEntity.id),
          })
          void queryClient.invalidateQueries({
            queryKey: getGetUserSharedDatasQueryKey(),
          })
          onClose()
        },
        onError: genericErrorToast,
      }
    )
  }

  return (
    <Modal
      {...{ isOpen, onClose }}
      title={<FormattedMessage id="userSharedData.modal.title_cancel" />}
      scrollBehavior="outside"
    >
      <form onSubmit={handleSubmit(postShareRequest)}>
        <ModalBody>
          <FormControl as={Flex} flexDir="column" gap="6">
            <Box>
              <FormattedMessage id="userSharedData.cancel.message" />
            </Box>

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.object" />
              </FormLabel>
              <SharedEntityName item={sharedEntity} displayInputLike={true} />
            </Box>

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.comment" />
              </FormLabel>
              <Textarea {...register("messageAnswer")} variant="outlineWithBorder" />
            </Box>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button display="flex" gap="2" type="submit" isLoading={isPending}>
            <FormattedMessage id="confirm" />
            <TelegramAltIcon boxSize={4} />
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
